import Modal from "bootstrap/js/dist/modal";
import Device from "./Device";
// Define this object for external usage of Bootstrap 5 Modal instance
window.Modal = Modal;
function areModalsClosed() {
    const openModals = document.querySelectorAll(".modal.show").length;
    return openModals === 0;
}
function isModalShown(modalElement) {
    return modalElement.classList.contains("show");
}
function showModal(modalElement) {
    Modal.getOrCreateInstance(modalElement).show();
}
function hideModal(modalElement) {
    Modal.getOrCreateInstance(modalElement).hide();
}
let iosSavedScrollY = null;
// iOS Safari ignores `overflow: hidden` on body. Use the `position: fixed`
// hack instead to not allow to scroll the body content when a modal is open.
function lockBodyScrollIOS() {
    if (iosSavedScrollY !== null)
        return; // already locked — don't overwrite saved position
    iosSavedScrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${iosSavedScrollY}px`;
    document.body.style.width = "100%";
}
function unlockBodyScrollIOS() {
    if (iosSavedScrollY === null)
        return; // not locked
    const scrollY = iosSavedScrollY;
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    window.scrollTo(0, scrollY);
    iosSavedScrollY = null;
}
// Applies or removes scroll lock on the body based on whether any modals are currently open.
// Overrides default Bootstrap behavior to also handle sequential and nested modals.
function handleScrollLock(type) {
    switch (type) {
        case "shown":
        case "hidden":
            if (areModalsClosed()) {
                document.body.classList.remove("modal-open");
                document.body.style.overflow = "";
                if (Device.getOSName() === "iOS") {
                    unlockBodyScrollIOS();
                }
            }
            else {
                document.body.classList.add("modal-open");
                document.body.style.overflow = "hidden";
                if (Device.getOSName() === "iOS") {
                    lockBodyScrollIOS();
                }
            }
            break;
    }
}
// e2e synchronization — tracks in-flight show/hide operations so test runners
// can wait for animations to settle before asserting on the DOM.
const addTask = "__addTask__" in window
    ? window.__addTask__
    : () => { };
const doneTask = "__doneTask__" in window
    ? window.__doneTask__
    : () => { };
function handleTaskTracking(type, el) {
    switch (type) {
        case "show":
            addTask(`Modals:show ${el.id}`);
            break;
        case "shown":
            doneTask(`Modals:show ${el.id}`);
            break;
        case "hide":
            addTask(`Modals:hide ${el.id}`);
            break;
        case "hidden":
            doneTask(`Modals:hide ${el.id}`);
            break;
    }
}
/**
 * Modals that have started hiding (hide.bs.modal) but not yet finished (hidden.bs.modal).
 * Used by waitForPendingHides to sequence show animations after hide animations complete.
 */
const pendingHideIds = new Set();
function handlePendingHides(type, el) {
    switch (type) {
        case "hide":
            pendingHideIds.add(el.id);
            break;
        case "hidden":
            pendingHideIds.delete(el.id);
            break;
    }
}
/**
 * Returns a promise that resolves once all currently-hiding modals have finished
 * their hide animations.
 */
function waitForPendingHides() {
    return new Promise((resolve) => {
        // Uses setTimeout to make it work with immediate show -> hide
        setTimeout(() => {
            if (pendingHideIds.size === 0) {
                resolve();
                return;
            }
            const promises = [...pendingHideIds].map((id) => new Promise((res) => {
                const el = document.getElementById(id);
                if (!el) {
                    res();
                    return;
                }
                el.addEventListener("hidden.bs.modal", () => res(), { once: true });
            }));
            void Promise.all(promises).then(() => resolve());
        }, 0);
    });
}
/**
 * Attach document-level listeners from Bootstrap Modals
 */
function initModalListeners() {
    const dispatch = (type) => (e) => {
        const el = e.target;
        handleScrollLock(type);
        handleTaskTracking(type, el);
        handlePendingHides(type, el);
    };
    document.addEventListener("show.bs.modal", dispatch("show"));
    document.addEventListener("shown.bs.modal", dispatch("shown"));
    document.addEventListener("hide.bs.modal", dispatch("hide"));
    document.addEventListener("hidden.bs.modal", dispatch("hidden"));
}
export default {
    init: initModalListeners,
    areModalsClosed,
    isModalShown,
    showModal,
    hideModal,
    waitForPendingHides,
};
